import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import { ApiEndpoints } from '../../services/ApiUrls';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

interface Notification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  related_object_type: string;
  related_object_id: string;
  is_read: boolean;
  created_at: string;
}

const NotificationListPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get(ApiEndpoints.notifications.list);
      setNotifications(response.data.results || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      enqueueSnackbar('Failed to load notifications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.post(ApiEndpoints.notifications.markAsRead(id));
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === id ? { ...notification, is_read: true } : notification
        )
      );
      enqueueSnackbar('Notification marked as read', { variant: 'success' });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      enqueueSnackbar('Failed to mark notification as read', { variant: 'error' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post(ApiEndpoints.notifications.markAllAsRead);
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => ({ ...notification, is_read: true }))
      );
      enqueueSnackbar('All notifications marked as read', { variant: 'success' });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      enqueueSnackbar('Failed to mark all notifications as read', { variant: 'error' });
    }
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'VEHICLE_ENTRY':
        return 'primary';
      case 'VEHICLE_EXIT':
        return 'secondary';
      case 'TRANSFER_STATUS_CHANGE':
        return 'success';
      case 'ROUTE_VIOLATION':
        return 'error';
      default:
        return 'default';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'VEHICLE_ENTRY':
        return 'Vehicle Entry';
      case 'VEHICLE_EXIT':
        return 'Vehicle Exit';
      case 'TRANSFER_STATUS_CHANGE':
        return 'Transfer Status Change';
      case 'ROUTE_VIOLATION':
        return 'Route Violation';
      default:
        return type;
    }
  };

  const filteredNotifications = notifications.filter(notification =>
    notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.notification_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Grid container justifyContent="space-between" alignItems="center" mb={3}>
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Notifications
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" alignItems="center">
              <TextField
                placeholder="Search notifications..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mr: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Mark all as read">
                <IconButton color="primary" onClick={handleMarkAllAsRead}>
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredNotifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No notifications found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <TableRow 
                        key={notification.id}
                        sx={{ 
                          backgroundColor: notification.is_read ? 'inherit' : 'rgba(25, 118, 210, 0.08)'
                        }}
                      >
                        <TableCell>{notification.title}</TableCell>
                        <TableCell>{notification.message}</TableCell>
                        <TableCell>
                          <Chip
                            label={getNotificationTypeLabel(notification.notification_type)}
                            color={getNotificationTypeColor(notification.notification_type) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {format(new Date(notification.created_at), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={notification.is_read ? 'Read' : 'Unread'}
                            color={notification.is_read ? 'default' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {!notification.is_read && (
                            <Tooltip title="Mark as read">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleMarkAsRead(notification.id)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default NotificationListPage;